// frontend/src/pages/AuthPage.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Fade,
  Slide,
  Typography,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LoginForm from '../components/Auth/LoginForm';
import RegisterForm from '../components/Auth/RegisterForm';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const handleAuthSuccess = () => {
    // Redirect to the page user was trying to access, or home
    const from = location.state?.from?.pathname || '/';
    navigate(from, { replace: true });
  };

  const switchToRegister = () => {
    setIsLogin(false);
  };

  const switchToLogin = () => {
    setIsLogin(true);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={800}>
          <Box>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  mb: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                {t('auth.title')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }}
              >
                {t('auth.subtitle')}
              </Typography>
            </Box>

            {/* Auth Forms */}
            <Box sx={{ position: 'relative', minHeight: isLogin ? '500px' : '800px' }}>
              <Slide
                direction="right"
                in={isLogin}
                mountOnEnter
                unmountOnExit
                timeout={300}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    top: 0,
                    zIndex: isLogin ? 2 : 1,
                  }}
                >
                  <LoginForm
                    onSwitchToRegister={switchToRegister}
                    onLoginSuccess={handleAuthSuccess}
                  />
                </Box>
              </Slide>

              <Slide
                direction="left"
                in={!isLogin}
                mountOnEnter
                unmountOnExit
                timeout={300}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    top: 0,
                    zIndex: !isLogin ? 2 : 1,
                  }}
                >
                  <RegisterForm
                    onSwitchToLogin={switchToLogin}
                    onRegisterSuccess={handleAuthSuccess}
                  />
                </Box>
              </Slide>
            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255,255,255,0.8)',
                    mb: 1,
                  }}
                >
                  {t('auth.securityNote')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                  }}
                >
                  {t('auth.dataProtection')}
                </Typography>
              </Paper>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default AuthPage;
